import type { CompanyAutofill } from "./types";

// Shared with the web form, so it must stay free of Node-only imports.
export const LOOKUP_DEBOUNCE_MS = 600;

export const AUTOFILL_FIELDS = ["companyName", "businessUnit", "pricingPlan"] as const;
export type AutofillField = (typeof AUTOFILL_FIELDS)[number];

export interface AutofillPatch {
  // New values for the fields to change. An empty string clears a field an earlier suggestion filled.
  values: Partial<Record<AutofillField, string>>;
  // The fields this suggestion filled, for the "check before submitting" note.
  filled: AutofillField[];
}

// What Vestd knows about the company always wins: a value in the suggestion replaces whatever is in the field, even something
// the person typed. The person can change it afterwards. A field the suggestion has no value for keeps what the person
// typed; only a stale value an earlier suggestion supplied (and the person has not edited) is cleared.
export function applyAutofill(
  suggestion: CompanyAutofill,
  touched: ReadonlySet<AutofillField>,
  previouslyFilled: ReadonlySet<AutofillField> = new Set(),
): AutofillPatch {
  const values: AutofillPatch["values"] = {};
  const filled: AutofillField[] = [];
  for (const field of AUTOFILL_FIELDS) {
    const value = suggestion[field];
    if (value) {
      values[field] = value;
      filled.push(field);
    } else if (previouslyFilled.has(field) && !touched.has(field)) {
      values[field] = "";
    }
  }
  return { values, filled };
}

// Called when the URL changes, or a lookup fails or finds nothing: the earlier suggestion no longer belongs to this URL, so the
// fields it filled are emptied, except any the person has edited since.
export function clearSuggestions(touched: ReadonlySet<AutofillField>, previouslyFilled: ReadonlySet<AutofillField>): AutofillPatch {
  return applyAutofill({}, touched, previouslyFilled);
}
